/**
 * Состав сессии и голос – два разных запроса, и до сих пор каждая поверхность сшивала их у себя.
 * Главный вопрос участника («я один?») от этого получал три разных ответа: полоса считала
 * подключённых по серверному снимку, окно – по живому составу LiveKit, а списка «кто вообще в
 * сборе» не было ни там, ни там.
 *
 * Здесь одно слияние на всех и без React: чистую функцию можно проверить тестом на все гонки,
 * которые в интерфейсе ловятся вручную раз в квартал.
 */

/** Принятый участник сессии. Единственный источник имени и аватара – см. `merge` ниже. */
export type RosterMemberInput = {
  userId?: string | null;
  name?: string | null;
  avatar?: string | null;
};

export type RosterInput = {
  /** Уже отфильтрованные принятые участники: кого пускать в состав, решает вызывающий. */
  members?: readonly RosterMemberInput[] | null;
  hostId?: string | null;
  /**
   * Имя и аватар хоста на случай, когда его строка ещё не приехала в составе (устаревший кеш):
   * то же правило, что в `SessionDetailsDialog` – у сбора не бывает секунды без автора.
   */
  hostName?: string | null;
  hostAvatar?: string | null;
};

/**
 * Голос из любого из двух источников: живой `voiceStore.members`, пока я в комнате, и серверный
 * снимок `getVoiceRoomQueryOptions`, пока нет. Оба несут `userId` и `connected`; снимок не знает
 * только того, что живёт десятки раз в секунду, – кто говорит и у кого выключен микрофон.
 */
export type VoiceMemberInput = {
  userId?: string | null;
  connected?: boolean | null;
  speaking?: boolean | null;
  muted?: boolean | null;
  displayName?: string | null;
  avatar?: string | null;
};

export type RosterVoiceMember = {
  userId: string;
  name: string | null;
  avatar: string | null;
  isHost: boolean;
  inVoice: boolean;
  speaking: boolean;
  muted: boolean;
};

export type RosterVoice = {
  members: RosterVoiceMember[];
  /** Сколько человек слышно прямо сейчас. */
  connected: number;
  /** Сколько человек в составе. Числитель может обогнать знаменатель – см. гонку после исключения. */
  total: number;
};

/**
 * Человек, а не соединение: LiveKit-identity выглядит как `<userId>#<хвост устройства>`, и вторая
 * вкладка того же аккаунта занимала бы второе место из пяти. `voiceStore` схлопывает такие
 * идентификаторы у себя, но серверный снимок и будущие источники об этом не знают – правило
 * повторено здесь, чтобы слияние нельзя было накормить сырыми identity.
 */
const personIdOf = (identity: string) => identity.split('#')[0] || identity;

type LiveState = {
  speaking: boolean;
  muted: boolean;
  name: string | null;
  avatar: string | null;
};

/** Голос по людям: несколько устройств одного человека сворачиваются в одну запись. */
const collapseVoice = (voice: readonly VoiceMemberInput[] | null | undefined) => {
  const live = new Map<string, LiveState>();

  for (const entry of voice ?? []) {
    if (!entry?.userId) {
      continue;
    }
    // Серверный снимок помнит и тех, кто уже вышел: в комнате они числятся, но их не слышно.
    if (entry.connected === false) {
      continue;
    }

    const userId = personIdOf(entry.userId);
    const previous = live.get(userId);
    live.set(userId, {
      speaking: Boolean(previous?.speaking) || Boolean(entry.speaking),
      // Замьючен – только если молчат ВСЕ его соединения: с телефона человек может говорить,
      // пока вкладка на компьютере стоит выключенной.
      muted: (previous ? previous.muted : true) && Boolean(entry.muted),
      name: previous?.name ?? entry.displayName ?? null,
      avatar: previous?.avatar ?? entry.avatar ?? null,
    });
  }

  return live;
};

/**
 * Один список «состав + голос» и счёт «сколько нас слышно из скольких».
 *
 * Правила, каждое из которых оплачено багом:
 * – хост первым, даже если сервер ещё не вернул его строку в составе;
 * – имя и аватар – из состава, а не из токена LiveKit: токен выдаётся на входе и с тех пор
 *   переименования профиля не знает;
 * – дубли по `userId` отсекаются: React по одинаковому key молча выкидывает строку, а счётчик
 *   продолжает считать её по длине массива;
 * – тот, кто в голосе, но уже не в составе (гонка между исключением и разрывом медиа), из списка
 *   не пропадает – его слышно, и назвать его нечем, кроме имени из голоса.
 */
export const mergeRosterVoice = (
  roster: RosterInput | null | undefined,
  voice: readonly VoiceMemberInput[] | null | undefined
): RosterVoice => {
  const live = collapseVoice(voice);
  const hostId = roster?.hostId ? personIdOf(roster.hostId) : null;

  const members: RosterVoiceMember[] = [];
  const seen = new Set<string>();

  const push = (
    userId: string,
    name: string | null | undefined,
    avatar: string | null | undefined,
    isHost: boolean
  ) => {
    if (!userId || seen.has(userId)) {
      return;
    }
    seen.add(userId);

    const voiceState = live.get(userId);
    members.push({
      userId,
      // Имя из голоса – только когда состав молчит вовсе: пустая строка вместо ника хуже, чем
      // имя из токена.
      name: name ?? voiceState?.name ?? null,
      avatar: avatar ?? voiceState?.avatar ?? null,
      isHost,
      inVoice: Boolean(voiceState),
      speaking: Boolean(voiceState?.speaking),
      muted: Boolean(voiceState?.muted),
    });
  };

  const rosterMembers = (roster?.members ?? []).filter(
    (member): member is RosterMemberInput => Boolean(member?.userId)
  );

  if (hostId) {
    const hostRow = rosterMembers.find((member) => personIdOf(member.userId!) === hostId);
    push(
      hostId,
      hostRow?.name ?? roster?.hostName ?? null,
      hostRow?.avatar ?? roster?.hostAvatar ?? null,
      true
    );
  }

  for (const member of rosterMembers) {
    push(personIdOf(member.userId!), member.name ?? null, member.avatar ?? null, false);
  }

  const total = members.length;

  // Хвост списка – те, кого слышно, но в составе уже нет. Они идут после состава: это аварийное
  // состояние на несколько секунд, а не полноправная строка.
  for (const [userId, voiceState] of live) {
    push(userId, voiceState.name, voiceState.avatar, false);
  }

  return {
    members,
    connected: members.filter((member) => member.inVoice).length,
    total,
  };
};
